import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { AreaGroup, CreateAreaGroup, UpdateAreaGroup } from '@estimator/shared';

export function useAreaGroups(projectId: string | undefined) {
  return useQuery({
    queryKey: ['area-groups', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<AreaGroup[]>('/area-groups', {
        params: { project_id: projectId },
      });
      return data;
    },
    enabled: !!projectId,
  });
}

export function useCreateAreaGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateAreaGroup) => {
      const { data } = await apiClient.post<AreaGroup>('/area-groups', body);
      return data;
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: ['area-groups', vars.project_id] }),
  });
}

export function useUpdateAreaGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateAreaGroup & { id: string }) => {
      const { data } = await apiClient.patch<AreaGroup>(`/area-groups/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-groups'] }),
  });
}

export function useDeleteAreaGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/area-groups/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-groups'] }),
  });
}

export function useReorderAreaGroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await apiClient.patch('/area-groups/reorder', { ids });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-groups'] }),
  });
}
